<template>
  <cv-overflow-menu ref="overflowMenuRef" class="overflow-menu" :class="cssClasses">
    <template v-for="slot in Object.keys($slots)" #[slot]="slotData">
      <slot :name="slot" v-bind="slotData || {}" />
    </template>
  </cv-overflow-menu>
</template>

<script setup lang="ts">
import { ComponentPublicInstance, computed, onMounted, useAttrs, useTemplateRef } from "vue";
import DOMUtils from "@utils/dom.utils.ts";

const overflowMenuRef = useTemplateRef<ComponentPublicInstance>("overflowMenuRef");
const attrs = useAttrs();
const cssClasses = computed(() => {
  return {
    disabled: attrs.disabled,
  };
});

onMounted(() => {
  if (attrs.tabindex === "-1" && overflowMenuRef.value) {
    const elem = DOMUtils.elOf(
      overflowMenuRef.value.$el,
      ".bx--overflow-menu__trigger.bx--tooltip__trigger.bx--tooltip--a11y",
    )[0];
    DOMUtils.setAttr(elem, "tabindex", "-1");
  }
});

const triggerClick = () => {
  // do not remove, used in parent ref call
  const overflowMenu = overflowMenuRef.value;
  const overflowMenuTrigger = overflowMenu?.$refs.trigger as HTMLElement;
  if (overflowMenuTrigger) {
    overflowMenuTrigger.click();
  }
};

defineExpose({
  triggerClick,
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/typo";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.bx--overflow-menu {
  width: 40px;
  height: 40px;
  border-radius: 100%;

  &.disabled {
    cursor: not-allowed;

    &:hover {
      background-color: inherit;
    }

    .bx--overflow-menu__trigger {
      &:hover {
        cursor: not-allowed;
      }

      &:focus,
      &:active {
        pointer-events: none;
      }

      .bx--overflow-menu__icon {
        fill: $disabled-02;
      }

      .sls-icon {
        [fill] {
          fill: $disabled-02;
        }
      }
    }
  }

  .bx--overflow-menu__trigger {
    &.bx--tooltip--a11y {
      &.bx--tooltip__trigger {
        width: inherit;
        height: inherit;
        border-radius: 100%;
        &:focus {
          outline: transparent;
          border: 1px solid $focus;
          border-radius: 100%;
        }

        &[aria-expanded="true"] {
          background-color: $ui-01;
        }
      }
    }

    &:hover,
    &:focus {
      .bx--overflow-menu__icon {
        fill: $interactive-01;
      }
    }

    .bx--overflow-menu__icon {
      fill: $interactive-01;
    }

    .sls-icon {
      [fill] {
        fill: $interactive-01;
      }
    }

    > svg {
      width: 24px;
      height: 24px;
    }
  }
}

// The overflow menu options is nested as a direct child of the document body
.bx--overflow-menu-options {
  border-radius: 10px;

  &.bx--overflow-menu-options--open {
    margin-top: $spacing-03;
    width: 11rem;

    // to overwrite boxshadow bg colour
    box-shadow: 0 2px 6px #0003;

    &:has(.edit-overflow-item) {
      width: 176px;
    }

    &.is-up {
      margin-top: (-$spacing-06);

      &::before {
        top: unset;
        bottom: -0.25rem;
      }
    }

    &.bx--overflow-menu--flip {
      &::before {
        right: 0.9rem;
        left: unset;
      }
    }
    &::before {
      content: "";
      display: block;
      height: 10px;
      width: 10px;
      background-color: $ui-01;
      position: absolute;
      top: -0.25rem;
      left: 0.9rem;
      transform: rotate(45deg);
      z-index: -1;
    }
  }

  &[data-type="calendar-event-attachment-overflow-menu"] {
    &::before {
      left: 15px;
    }
  }

  .divider {
    height: 1px;
    margin: 0 $spacing-03;
    background-color: $ui-03;
  }
}
</style>
